// ============================================================
// utils.js
//
// Helper utilities for placing cloned <g> blocks at the position
// of placeholder objects in arbitrary nested SVG coordinate systems.
//
// This uses a robust screen-space alignment method:
//
// 1. Insert clone temporarily so getScreenCTM() & getBBox() are valid.
// 2. Strip transforms from clone → now in its own local geometry.
// 3. Measure clone and placeholder bboxes in *screen coordinates*.
// 4. Compute screen-space delta that aligns their visual anchors.
// 5. Convert this delta back into the parent group's coordinate system.
// 6. Apply a simple translate(...) to the clone.
// 7. Remove placeholder.
//
// This avoids all CTM composition pitfalls when templates were authored
// in global space and placeholders are located in deep nested groups.
//
// ============================================================


// Generate unique instance IDs
export function uniqueReuseId(base) {
  return `${base}__${Math.random().toString(36).slice(2, 8)}`;
}


// Remove all transform attributes from a group and its descendants
export function stripAllTransforms(root) {
  root.removeAttribute('transform');
  root.querySelectorAll('[transform]').forEach(n => n.removeAttribute('transform'));
}

// Compute screen-space bbox (important: getScreenCTM maps local coords → screen)
export function bboxInScreen(el) {
  const bb = el.getBBox();
  const m = el.getScreenCTM();
  if (!m) return { x: 0, y: 0, width: 0, height: 0 };

  const x0 = bb.x, y0 = bb.y;
  const x1 = bb.x + bb.width, y1 = bb.y + bb.height;

  const p00 = { x: m.a*x0 + m.c*y0 + m.e, y: m.b*x0 + m.d*y0 + m.f };
  const p11 = { x: m.a*x1 + m.c*y1 + m.e, y: m.b*x1 + m.d*y1 + m.f };

  return { x: p00.x, y: p00.y, width: p11.x - p00.x, height: p11.y - p00.y };
}




// Align clone to placeholder: top-left anchor, scaled to fit the placeholder.
//
// The placeholder is a rect the author drew to say where the block goes. Its
// SIZE was measured here and then thrown away — only the corner was used, and
// the block always drew at whatever size it happened to be in the score it was
// defined in. Hosts do not share a scale: a score sized to the viewport height
// from a 640-unit viewBox draws at ~1.6x, a page declaring width/height draws
// at 1:1, so the same menu came out visibly bigger in one than the other with
// no way to say otherwise.
//
// Honouring the size is the control that was missing. Draw the placeholder the
// size you want the block and it is scaled to fit — same block, right size on
// every host. A placeholder drawn at the block's own size, which is what
// demo-reuse does, scales by exactly 1 and nothing moves.
//
// Contain rather than stretch, and uniform: a GUI block has an aspect ratio
// worth keeping, and fitting the smaller ratio can never overflow the rect the
// author drew.
export function alignCloneAtPlaceholder_TopLeft(clone, placeholder) {
  const parent = placeholder.parentNode;
  const bbPh = bboxInScreen(placeholder);
  const bbCl = bboxInScreen(clone);

  let s = 1;
  if (bbCl.width > 0 && bbCl.height > 0 && bbPh.width > 0 && bbPh.height > 0) {
    s = Math.min(bbPh.width / bbCl.width, bbPh.height / bbCl.height);
  }
  if (!Number.isFinite(s) || s <= 0) s = 1;

  // Solve in the parent's coordinates: the transform maps local L to s*L + t,
  // so t is the placeholder's corner less the scaled clone corner. Doing it
  // here rather than as a screen delta keeps the scale and the translation in
  // the one space they are actually applied in.
  const Ms = parent.getScreenCTM();
  if (!Ms) return;
  const Inv = Ms.inverse();
  const toParent = (x, y) => ({
    x: Inv.a * x + Inv.c * y + Inv.e,
    y: Inv.b * x + Inv.d * y + Inv.f,
  });
  const ph = toParent(bbPh.x, bbPh.y);
  const cl = toParent(bbCl.x, bbCl.y);

  const tx = ph.x - s * cl.x;
  const ty = ph.y - s * cl.y;
  clone.setAttribute('transform', `matrix(${s} 0 0 ${s} ${tx} ${ty})`);
}
